// SPDX-License-Identifier: Elastic-2.0
/**
 * Locale-aware formatting helpers.
 *
 * Thin wrappers around `Intl.NumberFormat`, `Intl.DateTimeFormat`, and
 * `Intl.RelativeTimeFormat` keyed off the active UI locale (functional
 * spec § 9 — "Number/date formatting").
 *
 * Why a wrapper instead of `t()`-style ICU plurals: most surfaces want
 * raw formatted numbers/dates (sensor readings, timestamps, energy
 * usage). i18next handles plural copy; this handles the values inside.
 *
 * Implementation notes:
 *  - We resolve the locale lazily on each call via `useLocale.getState()`
 *    so callers don't need to be hooks and don't re-render on switch.
 *    Components that DO need re-render on locale change should call
 *    `useLocale()` themselves and pass `locale` to a memoized formatter.
 *  - `Intl.*` formatter construction is fast enough for hot paths in
 *    practice; if profiling later shows otherwise, swap in a per-locale
 *    LRU cache here without changing the call sites.
 */
import { useLocale, type LocaleState } from "@/i18n/useLocale";

/** Resolve the active locale from the Zustand store. */
function activeLocale(): LocaleState["locale"] {
  return useLocale.getState().locale;
}

/**
 * Format a number for the active locale.
 *
 * @example
 *   formatNumber(1234.5)                       // EN → "1,234.5"
 *   formatNumber(1234.5)                       // ES → "1.234,5"
 *   formatNumber(0.42, { style: "percent" })   // EN → "42%"
 */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(activeLocale(), options).format(value);
}

/**
 * Format a date for the active locale.
 *
 * Accepts `Date`, ISO string, or epoch millis (the same shapes the
 * F-300 gateway emits in WS payloads).
 */
export function formatDate(
  value: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(activeLocale(), options).format(date);
}

/**
 * Format a relative time (e.g. "2 minutes ago", "in 3 hours").
 *
 * Picks the smallest unit that produces a non-zero rounded value, in the
 * order: second → minute → hour → day. Larger units (week/month/year)
 * are intentionally omitted at MVP — dashboard surfaces are bounded to
 * "last 24h" windows.
 */
const RELATIVE_UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ["second", 1_000],
  ["minute", 60_000],
  ["hour", 3_600_000],
  ["day", 86_400_000],
];

export function formatRelativeTime(value: Date | string | number, now: Date = new Date()): string {
  const target = value instanceof Date ? value : new Date(value);
  const diffMs = target.getTime() - now.getTime();
  let chosenUnit: Intl.RelativeTimeFormatUnit = "second";
  let chosenValue = 0;
  for (const [unit, msPerUnit] of RELATIVE_UNITS) {
    const v = diffMs / msPerUnit;
    if (Math.abs(v) >= 1 || unit === "second") {
      chosenUnit = unit;
      chosenValue = v;
    }
  }
  const rounded = Math.round(chosenValue);
  return new Intl.RelativeTimeFormat(activeLocale(), { numeric: "auto" }).format(
    rounded,
    chosenUnit,
  );
}
